import { useMemo, useState } from "react";
import { useResource } from "@/hooks/useResource";
import { LoadingState, EmptyState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { money } from "@/components/sales-reports/EnhancedReports";
import { Printer } from "lucide-react";

const STATUS_LABELS = { PAID: "Paid", PARTIALLY_PAID: "Part Paid", NOT_DUE: "Not Due", DUE: "Due", OVERDUE: "Overdue" };

const PRINT_CSS = `
@media print {
  body * { visibility: hidden !important; }
  #ms-print-area, #ms-print-area * { visibility: visible !important; }
  #ms-print-area { position: absolute !important; top: 0; left: 0; width: 100% !important;
    margin: 0 !important; padding: 0 !important; border: 0 !important; box-shadow: none !important; }
  @page { size: A4; margin: 12mm; }
}
`;

export default function MonthlyStatementTab() {
  const { data: buyersRaw } = useResource("/buyers");
  const buyers = Array.isArray(buyersRaw) ? buyersRaw : buyersRaw?.items || [];
  const [buyerId, setBuyerId] = useState("");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [applied, setApplied] = useState(null);

  const qs = useMemo(() => applied ? `?buyerId=${encodeURIComponent(applied.buyerId)}&month=${applied.month}` : null, [applied]);
  const { data, isLoading } = useResource(qs ? `/receivables-due/monthly-statement${qs}` : null);

  const b = data?.buyer || {};
  const c = data?.company || {};
  const p = data?.period || {};
  const t = data?.totals || {};
  const rows = data?.rows || [];

  return (
    <div className="space-y-4" data-testid="monthly-statement-tab">
      <style>{PRINT_CSS}</style>
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
        <div>
          <Label className="text-[10px]">Buyer</Label>
          <select data-testid="ms-buyer" value={buyerId} onChange={(e) => setBuyerId(e.target.value)} className="h-8 w-64 rounded-md border border-stone-200 bg-white px-2 text-xs">
            <option value="">Select buyer…</option>
            {buyers.map((x) => <option key={x.id || x._id} value={x.buyerId || x.id}>{x.parentCompany ? `${x.parentCompany} — ` : ""}{x.name || x.company} · {x.buyerId}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[10px]">Month</Label>
          <Input data-testid="ms-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-8 w-40 text-xs" />
        </div>
        <Button size="sm" className="h-8 bg-amber-700 hover:bg-amber-800 text-xs" data-testid="ms-generate" disabled={!buyerId || !month}
          onClick={() => setApplied({ buyerId, month })}>Generate Statement</Button>
        {data && (
          <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" data-testid="ms-print" onClick={() => window.print()}>
            <Printer className="h-3.5 w-3.5" /> Print / Save as PDF
          </Button>
        )}
        <p className="w-full text-[11px] text-stone-500">Invoices are listed by <b>Due Date</b> falling in the month. Use the browser print dialog to save as PDF and share with the buyer.</p>
      </div>

      {applied && (isLoading ? <LoadingState label="Building statement…" /> : !data ? null : (
        <div id="ms-print-area" data-testid="ms-statement" className="mx-auto max-w-4xl rounded-xl border border-stone-200 bg-white p-8 shadow-sm">
          {/* Header — freshezy branding */}
          <div className="flex items-start justify-between border-b-2 border-amber-800 pb-4">
            <div className="flex items-center gap-3">
              {c.logoDataUri
                ? <img src={c.logoDataUri} alt={c.brand || "freshezy"} className="h-12 w-auto object-contain" data-testid="ms-logo" />
                : <div className="font-display text-3xl font-black lowercase tracking-tight text-amber-800" data-testid="ms-brand">{c.brand || "freshezy"}</div>}
              <div>
                <div className="text-sm font-bold text-stone-900">{c.name}</div>
                {c.address && <div className="text-[10px] text-stone-500">{c.address}</div>}
                <div className="text-[10px] text-stone-500">
                  {[c.gstin ? `GSTIN ${c.gstin}` : "", c.phone, c.email].filter(Boolean).join(" · ")}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-base font-bold uppercase tracking-wide text-stone-900">Receivables Statement</div>
              <div className="text-xs font-semibold text-amber-800" data-testid="ms-period">{p.label}</div>
              <div className="text-[10px] text-stone-500">{p.from} to {p.to}</div>
            </div>
          </div>

          {/* Buyer block */}
          <div className="mt-4 flex items-start justify-between gap-4">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wide text-stone-400">Statement For</div>
              <div className="text-sm font-bold text-stone-900" data-testid="ms-buyer-name">{b.name}</div>
              <div className="text-[11px] text-stone-500">{b.buyerId}{b.gstin ? ` · GSTIN ${b.gstin}` : ""}</div>
              {b.address && <div className="text-[11px] text-stone-500">{b.address}</div>}
            </div>
            <div className="text-right text-[11px] text-stone-500">
              <div>Payment terms: <b className="text-stone-700">{b.paymentTermsDays} days</b></div>
              <div>Basis: Due Date (Invoice Date + terms)</div>
            </div>
          </div>

          {/* Summary strip */}
          <div className="mt-4 grid grid-cols-5 gap-2">
            {[["Brought Forward", t.broughtForward, `${t.broughtForwardInvoices || 0} inv`],
              ["Invoiced (due this month)", t.invoiceAmount, `${t.invoices || 0} inv`],
              ["Received (Bank/Cash)", t.received, ""],
              ["TDS Deducted", t.tds, ""],
              ["Closing Outstanding", t.closing, ""]].map(([k, v, hint]) => (
              <div key={k} className="rounded-lg border border-stone-200 bg-stone-50 px-2.5 py-2">
                <div className={`text-sm font-bold tabular-nums ${k === "Closing Outstanding" ? "text-amber-800" : "text-stone-900"}`}>{money(v)}</div>
                <div className="text-[9px] font-semibold uppercase tracking-wide text-stone-500">{k}{hint ? ` · ${hint}` : ""}</div>
              </div>
            ))}
          </div>

          {/* Invoice table */}
          {rows.length === 0 ? (
            <div className="mt-6"><EmptyState title="No invoices due this month" message="No receivables for this buyer become due in the selected month." /></div>
          ) : (
            <table className="mt-4 w-full text-xs" data-testid="ms-table">
              <thead>
                <tr className="border-y border-stone-300 bg-stone-100 text-left text-[10px] font-bold uppercase tracking-wide text-stone-600">
                  <th className="px-2 py-2">Due Date</th>
                  <th className="px-2 py-2">Invoice #</th>
                  <th className="px-2 py-2">Invoice Date</th>
                  <th className="px-2 py-2 text-right">Invoice Amt</th>
                  <th className="px-2 py-2 text-right">Cr. Notes</th>
                  <th className="px-2 py-2 text-right">Received</th>
                  <th className="px-2 py-2 text-right">TDS</th>
                  <th className="px-2 py-2 text-right">Balance</th>
                  <th className="px-2 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {rows.map((r, i) => (
                  <tr key={`${r.invoiceNumber}-${i}`} data-testid={`ms-row-${i}`}>
                    <td className="px-2 py-1.5 font-medium text-stone-800">{r.dueDate}</td>
                    <td className="px-2 py-1.5 font-mono">{r.invoiceNumber}</td>
                    <td className="px-2 py-1.5 text-stone-600">{r.invoiceDate}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{money(r.invoiceAmount)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.creditNotes ? money(r.creditNotes) : "—"}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.received ? money(r.received) : "—"}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-amber-700">{r.tds ? money(r.tds) : "—"}</td>
                    <td className="px-2 py-1.5 text-right font-semibold tabular-nums">{money(r.outstanding)}</td>
                    <td className="px-2 py-1.5 text-stone-600">{STATUS_LABELS[r.status] || r.status}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-stone-300 bg-stone-50 font-bold" data-testid="ms-totals-row">
                  <td className="px-2 py-2" colSpan={3}>TOTAL ({t.invoices || 0} invoices)</td>
                  <td className="px-2 py-2 text-right tabular-nums">{money(t.invoiceAmount)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{money(t.creditNotes)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{money(t.received)}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-amber-700">{money(t.tds)}</td>
                  <td className="px-2 py-2 text-right tabular-nums" data-testid="ms-total-outstanding">{money(t.outstanding)}</td>
                  <td />
                </tr>
                {(t.broughtForward || 0) > 0 && (
                  <tr className="bg-amber-50/60 font-bold">
                    <td className="px-2 py-2" colSpan={7}>Brought forward (overdue from earlier months) + this month</td>
                    <td className="px-2 py-2 text-right tabular-nums text-amber-800" data-testid="ms-closing">{money(t.closing)}</td>
                    <td />
                  </tr>
                )}
              </tfoot>
            </table>
          )}

          {/* Footer */}
          <div className="mt-6 border-t border-stone-200 pt-3 text-[10px] leading-relaxed text-stone-500">
            <p>{data.meta?.note}</p>
            <p className="mt-1">This is a system-generated statement from {c.name}. Generated on {new Date().toLocaleDateString("en-IN")}{data.meta?.generatedBy ? ` by ${data.meta.generatedBy}` : ""}. Please verify TDS certificates against the TDS column before remitting balance.</p>
          </div>
        </div>
      ))}
    </div>
  );
}
